import React from 'react';
import { useSelector } from 'react-redux';
import { ActionButton, DialogTrigger, Dialog, Heading, Content, Footer } from '@adobe/react-spectrum';
import ShowMenu from '@spectrum-icons/workflow/ShowMenu';
import './HeaderBar.css';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { useAuth } from 'react-oidc-context';
import { AvatarImage } from '../avatarImage/AvatarImage';
import { NavLink } from '../common/NavLink';
import { TableFlipIcon } from '../common/TableFlipIcon';
import { BLUESKY_URL, DISCORD_INVITE_URL, PATREON_URL } from '../../config/socialLinks';

/**
 * Site navigation, rendered twice from this one list: inline in the header
 * bar and inside the menu. CSS decides which copy shows at each width
 * (see the "Header collapse" block in HeaderBar.css):
 *   - primary links sit in the header from 900px up, in the menu below that;
 *   - secondary links and the social buttons sit in the header from 1280px
 *     up, in the menu below that.
 */
const NAV_LINKS = [
    { to: '/meta', label: 'cEDH META', menuLabel: 'cEDH meta', tier: 'primary' },
    { to: '/commanders', label: 'COMMANDERS', menuLabel: 'Commanders', tier: 'primary' },
    { to: '/users', label: 'USERS', menuLabel: 'Users', tier: 'primary' },
    { to: '/archetypes', label: 'ARCHETYPES', menuLabel: 'Archetypes', tier: 'primary' },
    { to: '/changelog', label: 'CHANGELOG', menuLabel: 'Changelog', tier: 'secondary' },
    { to: '/faq', label: 'FAQ', menuLabel: 'FAQ', tier: 'secondary' },
    {
        to: '/bot',
        label: 'BOT',
        menuLabel: 'Discord bot',
        tier: 'secondary',
        icon: '/resources/discord-mark.png',
    },
];

const REFERENCE_LINKS = [
    { to: '/powerlevels', menuLabel: 'Power levels' },
    { to: '/algorithm', menuLabel: 'The algorithm' },
];

const SOCIAL_LINKS = [
    { url: PATREON_URL, event: 'patreon_click', label: 'Support us on Patreon!', icon: '/resources/patreon.png' },
    { url: BLUESKY_URL, event: 'bluesky_click', label: 'Follow us on BlueSky!', icon: '/resources/bluesky.png' },
    { url: DISCORD_INVITE_URL, event: 'discord_click', label: 'Join our Discord!', icon: '/resources/discord.png' },
];

const logNav = (to) => logEvent(getAnalytics(), `header_open_${to}`);

function SocialButtons() {
    return SOCIAL_LINKS.map(({ url, event, label, icon }) => (
        <ActionButton
            key={url}
            onPress={() => {
                logEvent(getAnalytics(), event);
                window.open(url, '_blank', 'noopener');
            }}
            aria-label={label}
            isQuiet
            data-role="icon-circle-sm"
        >
            <img src={icon} alt="" />
        </ActionButton>
    ));
}

export function HeaderBar() {
    const auth = useAuth();

    const profileName = useSelector((state) => state?.profile?.metadata?.profileName);
    const avatarUrl = useSelector((state) => state?.profile?.metadata?.avatarUri);

    return (
        <header className="siteHeader">
            <div className="siteHeaderBar">
                <NavLink to="/" className="siteHeaderBrand" aria-label="CommanderSalt.com home">
                    <img src="/resources/chef-kiss-header.png" width="30" height="30" alt="" />
                    <span className="HeaderText">COMMANDERSALT.COM</span>
                </NavLink>
                <nav className="siteNav" aria-label="Main">
                    {NAV_LINKS.map((link, i) => (
                        <React.Fragment key={link.to}>
                            {link.tier === 'secondary' && NAV_LINKS[i - 1]?.tier === 'primary' && (
                                <span className="siteNavDivider navTier--secondary" aria-hidden="true" />
                            )}
                            <NavLink
                                to={link.to}
                                className={`navLinksHeader navTier--${link.tier}`}
                                onClick={() => logNav(link.to)}
                            >
                                {link.icon && <img src={link.icon} alt="" className="navLinksHeaderIcon" />}
                                {link.label}
                            </NavLink>
                        </React.Fragment>
                    ))}
                </nav>
                <div className="siteHeaderActions">
                    <div className="siteHeaderSocials navTier--secondary">
                        <SocialButtons />
                    </div>
                    <DialogTrigger type="popover" mobileType="tray">
                        <ActionButton
                            isQuiet
                            aria-label="Menu"
                            data-role="icon-circle-sm"
                            UNSAFE_className="appMenuTrigger"
                        >
                            <ShowMenu />
                            <AvatarImage size={25} avatarUrl={avatarUrl} />
                        </ActionButton>
                        {(close) => (
                            <Dialog UNSAFE_className="appMenuDialog">
                                <Heading UNSAFE_className="appMenuBrand">commandersalt</Heading>
                                <Content>
                                    <nav className="appMenu" aria-label="Site menu">
                                        {!auth?.isAuthenticated && !auth.isLoading && (
                                            <AppMenuSection label="Account">
                                                <AppMenuLink
                                                    as="button"
                                                    onClick={() => {
                                                        close();
                                                        void auth.signinRedirect();
                                                    }}
                                                >
                                                    Log in / Sign up
                                                </AppMenuLink>
                                                <AppMenuLink to="/verify-email" onNavigate={close}>
                                                    Verify email
                                                </AppMenuLink>
                                            </AppMenuSection>
                                        )}
                                        {auth.isAuthenticated && (
                                            <AppMenuSection label={profileName || 'Account'}>
                                                <AppMenuLink
                                                    to={`/user/id/${encodeURIComponent(profileName)}`}
                                                    onNavigate={() => {
                                                        close();
                                                        logNav('profile');
                                                    }}
                                                >
                                                    Profile
                                                </AppMenuLink>
                                                {[
                                                    ['/linkedaccounts', 'Linked accounts'],
                                                    ['/managedecks', 'Manage decks'],
                                                    ['/settings', 'Settings'],
                                                ].map(([to, label]) => (
                                                    <AppMenuLink
                                                        key={to}
                                                        to={to}
                                                        onNavigate={() => {
                                                            close();
                                                            logNav(to);
                                                        }}
                                                    >
                                                        {label}
                                                    </AppMenuLink>
                                                ))}
                                                <AppMenuLink
                                                    as="button"
                                                    onClick={() => {
                                                        close();
                                                        void auth.signoutSilent();
                                                        window.signOutRedirect();
                                                    }}
                                                >
                                                    Log out
                                                </AppMenuLink>
                                            </AppMenuSection>
                                        )}
                                        <AppMenuSection label="Browse" className="menuTier--primary">
                                            {[{ to: '/', menuLabel: 'Leaderboard' }, ...NAV_LINKS]
                                                .filter((link) => link.tier !== 'secondary')
                                                .map((link) => (
                                                    <MenuNavLink key={link.to} link={link} close={close} />
                                                ))}
                                        </AppMenuSection>
                                        <AppMenuSection label="Reference">
                                            {[
                                                ...REFERENCE_LINKS,
                                                ...NAV_LINKS.filter((l) => l.tier === 'secondary'),
                                            ].map((link) => (
                                                <MenuNavLink key={link.to} link={link} close={close} />
                                            ))}
                                        </AppMenuSection>
                                    </nav>
                                </Content>
                                <Footer UNSAFE_className="appMenuFooter menuTier--secondary">
                                    <SocialButtons />
                                </Footer>
                            </Dialog>
                        )}
                    </DialogTrigger>
                </div>
            </div>
        </header>
    );
}

/**
 * Red page banner. Its title is the page's single <h1>; it renders at the
 * top of <main> so "skip to content" lands on it.
 */
export function PageBanner() {
    const title = useSelector((state) => state?.app?.title);
    const route = useSelector((state) => state?.app?.route);
    const profileName = useSelector((state) => state?.profile?.metadata?.profileName);
    const headerTitle = route.path === '/profile' ? profileName : title;

    return (
        <div className="layered-image headerBannerWrap">
            <h1 className="leaderboard-title">{headerTitle}</h1>
            <TableFlipIcon color="#ffffff" className="headerTableFlip" />
        </div>
    );
}

function MenuNavLink({ link, close }) {
    return (
        <AppMenuLink
            to={link.to}
            className={link.tier ? `menuTier--${link.tier}` : undefined}
            onNavigate={() => {
                close();
                logNav(link.to);
            }}
        >
            {link.menuLabel}
        </AppMenuLink>
    );
}

function AppMenuSection({ label, className, children }) {
    return (
        <div className={['appMenuSection', className].filter(Boolean).join(' ')}>
            <div className="appMenuSectionLabel">{label}</div>
            <div className="appMenuSectionItems">{children}</div>
        </div>
    );
}

/**
 * Single nav row. Renders either as a router-aware NavLink (default) or
 * as a plain <button> when `as="button"` (used for auth flows that
 * trigger redirects rather than navigate). The seared/steel styling is
 * driven entirely by .appMenuLink in HeaderBar.css.
 */
function AppMenuLink({ to, onNavigate, onClick, as, className, children }) {
    const classes = ['appMenuLink', className].filter(Boolean).join(' ');
    if (as === 'button') {
        return (
            <button type="button" className={classes} onClick={onClick}>
                <span className="appMenuLinkLabel">{children}</span>
            </button>
        );
    }
    return (
        <NavLink to={to} className={classes} onClick={onNavigate}>
            <span className="appMenuLinkLabel">{children}</span>
        </NavLink>
    );
}
